import { ColorBox, Typography } from '@esgian/esgianui';
import React from 'react';
import * as XLSX from 'xlsx';
import moment from 'moment';

export const dateSortCompare = (a, b, order) => {
  const val1 = moment.utc(a.data, moment.ISO_8601, true).valueOf();
  const val2 = moment.utc(b.data, moment.ISO_8601, true).valueOf();

  const val1IsValid = !isNaN(val1);
  const val2IsValid = !isNaN(val2);

  // Handle nulls or invalid values
  if (!val1IsValid && !val2IsValid) return 0;
  if (!val1IsValid) return order === 'asc' ? -1 : 1;
  if (!val2IsValid) return order === 'asc' ? 1 : -1;

  // Normal compare
  return order === 'asc' ? val1 - val2 : val2 - val1;
};

export const getTableCellRenderCII = (value, theme) => {
  const {
    palette: {
      charts: { fiveColorsIndication }
    }
  } = theme;

  let color;
  switch (value) {
    case 'A':
      color = fiveColorsIndication[0];
      break;
    case 'B':
      color = fiveColorsIndication[1];
      break;
    case 'C':
      color = fiveColorsIndication[2];
      break;
    case 'D':
      color = fiveColorsIndication[3];
      break;
    case 'E':
      color = fiveColorsIndication[4];
      break;
    default:
      color = null;
  }
  return color !== null ? (
    <div
      style={{
        margin: '4px',
        minWidth: '2.5em',
        alignSelf: 'center',
        justifyContent: 'center',
        display: 'flex',
        width: 'fit-content',
        fontSize: '0.875rem',
        padding: '8px',
        borderRadius: '10px',
        backgroundColor: `${color}80`
      }}>
      <Typography variant={'body2'} bold>
        {value}
      </Typography>
    </div>
  ) : (
    <Typography sx={{ p: 1, m: '4px' }}>{'-'}</Typography>
  );
};
export const getTableCellRender = (
  max,
  min,
  value,
  inverted = false,
  allowZero = true,
  coloring = true
) => {
  let isMinOrMax = false;
  let color = 'success';
  if (max === value) {
    isMinOrMax = true;
    color = inverted ? 'error' : 'success';
  }
  if (min === value) {
    isMinOrMax = true;
    color = inverted ? 'success' : 'error';
  }
  if (min !== null && max !== null && min === max) {
    if (min === 0 && max === 0) {
      isMinOrMax = false;
    } else {
      isMinOrMax = true;
      color = 'info';
    }
  }

  if (!allowZero && value === 0) {
    return (
      <Typography variant={'caption'} sx={{ p: 1, m: '4px' }}>
        {'-'}
      </Typography>
    );
  }

  if (!coloring) {
    isMinOrMax = false;
  }

  return value !== null && value !== undefined ? (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {isMinOrMax ? (
        <ColorBox color={color}>
          <Typography variant={'caption'} bold>
            {value}
          </Typography>
        </ColorBox>
      ) : (
        <Typography variant={'caption'} sx={{ p: 1, m: '4px' }}>
          {value}
        </Typography>
      )}
    </div>
  ) : (
    <Typography variant={'caption'} sx={{ p: 1, m: '4px' }}>
      {'-'}
    </Typography>
  );
};

export const tableTitlePaddingLeft = {
  MUIDataTableToolbar: {
    styleOverrides: {
      root: {
        paddingLeft: 18
      }
    }
  }
};

export const tableFilterWidth = {
  MUIDataTableFilter: {
    styleOverrides: {
      root: {
        minWidth: 450
      }
    }
  }
};

// -------- Helper for canal direction ----------
export const getDirection = (value) => {
  return (
    ['Unknown', 'Eastbound', 'Westbound', 'Northbound', 'Southbound'][value] || 'Invalid value'
  );
};

/**
 * Converts a time string into decimal hours.
 * Supports formats: "DD.HH:mm:ss" and "HH:mm:ss".
 *
 * * @param {string} timeString - a string in the format of DD.HH:MM:SS and all the other available options that can be handled
 * * The timestring is not a timestamp but represents number of days hours minutes etc.
 * * @returns {number} - The decimal representation of hours.
 */

export function getCovertTimeToDays(timeString) {
  if (!timeString) return null;
  let days = 0;
  let timeStr = timeString;

  // If it contains a dot before the time, split days and time
  if (timeString.includes('.') && timeString.match(/^\d+\./)) {
    const parts = timeString.split('.');
    days = parseInt(parts[0], 10);
    timeStr = parts[1]; // e.g., "07:13:21.6086956"
  }

  const [hours = '0', minutes = '0', secondsFraction = '0'] = timeStr.split(':');
  const seconds = parseFloat(secondsFraction);

  const duration = moment.duration({
    days,
    hours: parseInt(hours),
    minutes: parseInt(minutes),
    seconds: seconds
  });

  return duration.asDays().toFixed(2);
}

export function getConvertTimeToHours(timeString) {
  if (!timeString) return 0;

  let days = 0;
  const parts = timeString.split('.');

  // If there's a day part
  if (parts.length === 2 && parts[1].includes(':')) {
    days = Number(parts[0]); // days part
    timeString = parts[1]; // remaining is time
  } else if (parts.length === 1) {
    timeString = parts[0];
  }

  const [hours, minutes, seconds] = timeString.split(':');
  // Calculate total hours: days*24 + hours + minutes/60 + seconds/3600
  const totalHours = days * 24 + Number(hours) + Number(minutes) / 60 + Number(seconds) / 3600;

  return parseFloat(totalHours.toFixed(2));
}

export function sanitizeFileName(fileName) {
  return fileName
    .replace(/[^a-zA-Z0-9-_]/g, '-') // Replace invalid characters with a hyphen
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export const isObject = (value) => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};
const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const getExportColumnValue = (col, row) => {
  let value = getNestedValue(row, col.name); //  support nested values
  // Apply default transformations if needed
  if (typeof col.options?.customDownloadValue === 'function') {
    value = col.options.customDownloadValue(value, row);
  } else if (typeof col.options?.customBodyRender === 'function') {
    let renderVal = col.options.customBodyRender(value, { rowData: row });
    if (typeof renderVal === 'string' || typeof renderVal === 'number') {
      value = renderVal;
    }
  }
  // Check if value is a string like "7 000" and convert to number
  if (typeof value === 'string') {
    const numericString = value.replace(/\s+/g, ''); // remove all spaces
    if (/^-?\d+(\.\d+)?$/.test(numericString)) {
      value = parseFloat(numericString);
    }
  }

  return value ?? '';
};

export const exportCSVFile = (columns, data, filename = 'data') => {
  // Ensure the filename ends with .csv
  let cleanFilename;
  if (typeof filename === 'string') {
    const trimmed = filename.trim();
    cleanFilename = trimmed !== '' ? trimmed : 'data';
  } else {
    cleanFilename = 'data';
  }
  const finalFilename = cleanFilename.toLowerCase().includes('.csv')
    ? cleanFilename
    : `${cleanFilename}.csv`;

  // Filter out columns where display is explicitly set to false
  const visibleColumns = columns.filter(
    (col) => col.options?.display !== false || col.options?.download
  );

  const header = visibleColumns.map((col) => JSON.stringify(col.label)).join(',') + '\n';

  // Create CSV rows using nested access for each column name
  const rows = data
    .map((row) =>
      visibleColumns
        .map((col) => {
          return JSON.stringify(getExportColumnValue(col, row));
        })
        .join(',')
    )
    .join('\n');

  // Combine header and rows into the CSV content
  const csvContent = header + rows;

  // Create a Blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', finalFilename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportExcelFile = (columns, data, filename = 'data') => {
  // Ensure the filename ends with .xlsx
  let cleanFilename;

  if (typeof filename === 'string') {
    const trimmed = filename.trim();
    if (trimmed !== '') {
      cleanFilename = trimmed;
    } else {
      cleanFilename = 'data';
    }
  } else {
    cleanFilename = 'data';
  }

  const finalFilename = cleanFilename.toLowerCase().endsWith('.xlsx')
    ? cleanFilename
    : `${cleanFilename}.xlsx`;

  // Filter out columns where display is explicitly set to false
  const visibleColumns = columns.filter(
    (col) => col.options?.display !== false || col.options?.download
  );

  // Create the header row using column labels for visible columns
  const header = visibleColumns.map((col) => col.label);

  // Create the data rows, where data for each column is accessed using the column name
  const rows = data.map((row) =>
    visibleColumns.map((col) => {
      return getExportColumnValue(col, row);
    })
  );

  // Combine header and data rows into a worksheet
  const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);

  // Calculate the maximum length for each column's content (header + data)
  // Set the column widths in the worksheet
  ws['!cols'] = visibleColumns.map((col, index) => {
    const maxLength = Math.max(
      header[index]?.length,
      ...rows.map((row) => (row[index] ?? '').toString().length)
    );
    return { wpx: Math.min(maxLength * 10, 200) }; // Multiply by 10 for padding, limit to max width of 200px
  });

  // Create a new workbook with the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  // Write the workbook to a file and trigger download
  XLSX.writeFile(wb, finalFilename);
};

export const sortByName = (order) => {
  return (obj1, obj2) => {
    const nameA = obj1.data?.name?.toLowerCase?.() ?? '';
    const nameB = obj2.data?.name?.toLowerCase?.() ?? '';
    if (nameA < nameB) return order === 'asc' ? -1 : 1;
    if (nameA > nameB) return order === 'asc' ? 1 : -1;
    return 0;
  };
};
